import React, { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-final-form';
import { useDispatch } from 'react-redux';
import { identity } from 'ramda';

import { InputReg } from '@interlayer/js/app/components/main/Registration/components/common/InputReg';

import { useTranslations } from '@core/js/hooks/i18n';

import { useGetWebContentData } from '@interlayer/js/app/components/system/WebContent/hooks/useGetWebContentData';
import { useConfigTag } from '@interlayer/js/app/hooks/useConfigTag';
import { useDynamicTags } from '@interlayer/js/app/hooks/useDynamicTags';
import { VALIDATION_FIELDS } from '@interlayer/js/app/hooks/useValidationRules/constants';
import { useRegistrationSelector } from '@interlayer/js/app/store/hooks/useRegistrationSelector';
import { setOccupationSuggestions } from '@interlayer/js/app/store/reducers/Registration/actions';
import { clsx } from '@interlayer/js/app/utils/clsx.util';
import { useClickOutSide } from '@interlayer/js/app/utils/common.util';
import { DYNAMIC_TAG_NAMES } from '@interlayer/js/app/utils/constants';
import { CONFIG_TAG_TYPE } from '@interlayer/js/components/system/TagManager/buzz/constants';
import { TOccupationConfigTagConfig } from '@interlayer/js/components/system/TagManager/buzz/types';

import { TOccupation, TOccupationFieldProps } from './types';
import { parseOccupationsList } from './utils';

import style from './OccupationField.scss';

export const OccupationField: React.FC<TOccupationFieldProps> = ({ classNames = {}, isOther = false, setIsOther = identity }) => {
    const form = useForm();
    const wrapperRef = useRef<HTMLDivElement>(null);
    const dispatch = useDispatch();
    const isSuggestionsSet = useRef(false);
    const [isActive, setIsActive] = useState(false);
    const [customOccupationSelected, setCustomOccupationSelected] = useState('');
    const [suggestions, setSuggestions] = useState<TOccupation[]>([]);
    const { occupationSuggestions } = useRegistrationSelector();
    const t = useTranslations('playtech.mobile.system.registration.');
    const occupationConfig = useConfigTag(CONFIG_TAG_TYPE.occupationConfig) as TOccupationConfigTagConfig;
    const { config: { additionalOccupationOptions = [] } = {} } = occupationConfig || {};
    const [dTagOccupationsListWebContentId] = useDynamicTags([DYNAMIC_TAG_NAMES.occupationsListWebContentId]);
    const { state: { isLoading, data } } = useGetWebContentData<string>({
        id: dTagOccupationsListWebContentId
    });
    const otherOccupation = useMemo(() => {
        return additionalOccupationOptions.find((option) => option.isOther);
    }, [additionalOccupationOptions]);

    useClickOutSide(wrapperRef, () => setIsActive(false));

    useEffect(() => {
        if (!isLoading && !isSuggestionsSet.current) {
            const preparedData = parseOccupationsList(data);

            if (!preparedData) {
                return;
            }

            dispatch(setOccupationSuggestions(preparedData));
            setSuggestions(preparedData);
            isSuggestionsSet.current = true;
        }
    }, [data, dispatch, isLoading]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.trim().toLowerCase();

        if (value.length >= 2) {
            setSuggestions(occupationSuggestions.filter(({ suggestion }) => suggestion.toLowerCase().includes(value)));
            setIsActive(true);
        } else {
            setIsActive(false);
        }
    };

    const handleSuggestionClick = (selectedSuggestion: string) => () => {
        const customSelected = additionalOccupationOptions.find(({ option }) => selectedSuggestion === option)?.option || '';
        const preparedSuggestion = selectedSuggestion.toLowerCase();

        form.change(VALIDATION_FIELDS.occupation, customSelected ? '' : selectedSuggestion);
        form.blur(VALIDATION_FIELDS.occupation);
        setIsActive(false);
        setSuggestions(
            occupationSuggestions.filter(({ suggestion }) => suggestion.toLowerCase().includes(preparedSuggestion))
        );
        setCustomOccupationSelected(customSelected);
        setIsOther(Boolean(customSelected));
        form.change('mainOccupation', customSelected ? selectedSuggestion : '');
    };

    const handleOtherChange = (event: ChangeEvent<HTMLInputElement>) => {
        const preparedOccupation = event.target.value.trim().toLowerCase();

        form.change(VALIDATION_FIELDS.occupation, `${customOccupationSelected} - ${preparedOccupation}`);
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        if (e.target.value.trim().length >= 2) {
            setIsActive(true);
        }
    };

    return (
        <div
            ref={wrapperRef}
            className={style.wrapper}
        >
            <InputReg
                name={isOther ? 'mainOccupation' : VALIDATION_FIELDS.occupation}
                onFocus={handleFocus}
                onChange={handleChange}
                placeholder={t('label.occupation')}
                classNames={{ control: clsx(style.control, classNames.control) }}
            />

            {isOther && (
                <>
                    <InputReg
                        name="otherOccupation"
                        onChange={handleOtherChange}
                        placeholder={t('label.otherOccupation')}
                        classNames={{ control: clsx(style.controlOther, classNames.controlOther) }}
                    />

                    <InputReg
                        type="hidden"
                        name={VALIDATION_FIELDS.occupation}
                        classNames={{ control: style.hidden }}
                    />
                </>
            )}

            <div className={clsx(style.suggestionsWrapper, classNames.suggestionsWrapper, { [style.isActive]: isActive })}>
                {suggestions?.length
                    ? suggestions.map(({ suggestion }) => (
                        <span
                            key={suggestion}
                            className={style.suggestion}
                            onClick={handleSuggestionClick(suggestion)}
                        >
                            {suggestion}
                        </span>
                    ))
                    : otherOccupation && (
                        <span
                            className={style.suggestion}
                            onClick={handleSuggestionClick(otherOccupation.option)}
                        >
                            {otherOccupation.option}
                        </span>
                    )
                }
            </div>
        </div>
    );
};
